import { useContext, useMemo } from "react";
import {
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { format } from "date-fns";
import { useRecords } from "../../../api/useRecords";
import { StaffWorkLogsContext } from "../StaffWorkLogsDetail";
import { computeVacationHistoryRows } from "../utils/holidaysSummary";
import { getVacationHistorySpans, hasUnknownAgreementType } from "../utils/employmentRun";
import { useAgreementTypeLabels } from "../utils/useAgreementTypeLabels";
import { formatDisplayHours } from "../utils/general";

type VacationHistoryModalProps = {
    open: boolean;
    onClose: () => void;
};

// Days at the legal daily_pattern scale, as a caption next to the hours — same convention in every column.
const DaysSuffix = ({ hours, dailyPattern }: { hours: number; dailyPattern: number }) => {
    if (dailyPattern <= 0) return null;
    const days = (hours / dailyPattern).toFixed(1).replace(".", ",");
    return (
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({days} d)
        </Typography>
    );
};

// Same reading and wording as the widget: over-booked → "Excedido em", under-booked → "Faltam marcar",
// else all booked.
const YearStateCell = ({ entitlementHours, bookedHours, dailyPattern }: { entitlementHours: number; bookedHours: number; dailyPattern: number }) => {
    const overbookedHours = bookedHours - entitlementHours;
    if (overbookedHours >= 0.5) {
        return (
            <Typography variant="body2" color="error.main" sx={{ whiteSpace: "nowrap" }}>
                Excedido em {formatDisplayHours(overbookedHours, 0)}
                <DaysSuffix hours={overbookedHours} dailyPattern={dailyPattern} />
            </Typography>
        );
    }
    if (-overbookedHours >= 0.5) {
        return (
            <Typography variant="body2" color="error.main" sx={{ whiteSpace: "nowrap" }}>
                Faltam marcar {formatDisplayHours(-overbookedHours, 0)}
                <DaysSuffix hours={-overbookedHours} dailyPattern={dailyPattern} />
            </Typography>
        );
    }
    return (
        <Typography variant="body2" color="success.main" sx={{ whiteSpace: "nowrap" }}>
            Tudo marcado
        </Typography>
    );
};

export const VacationHistoryModal = ({ open, onClose }: VacationHistoryModalProps) => {
    const { staffAgreement, selectedYear, userId } = useContext(StaffWorkLogsContext);

    const weeklyHoursSchedule = staffAgreement?.staff_weekly_hours ?? [];
    const vacationFromFirstRow = Boolean(staffAgreement?.vacation_from_first_row);
    const historySpans = hasUnknownAgreementType(weeklyHoursSchedule) ? [] : getVacationHistorySpans(weeklyHoursSchedule, vacationFromFirstRow);
    const spanStartDate = historySpans.length > 0 ? format(historySpans[0].startDate, "yyyy-MM-dd") : null;

    // History deliberately queries back to the span start — before the 2026 live floor the widget uses.
    const { data: historyEntries } = useRecords("time_off", "start_date,end_date,type", {
        extraOptions: {
            filter: {
                _and: [
                    { type: { _eq: "holiday" } },
                    { user: { id: { _eq: userId } } },
                    { start_date: { _gte: spanStartDate } },
                    { start_date: { _lt: `${selectedYear + 1}-01-01` } },
                ],
            },
            pageSize: -1,
        },
        enabled: Boolean(open && userId && spanStartDate),
    });

    const baselineDays = staffAgreement?.unused_vacation_days_at_31_dec_2025;
    const agreementTypeLabels = useAgreementTypeLabels();

    const historyRows = useMemo(() => {
        const rows = computeVacationHistoryRows({
            weeklyHoursSchedule,
            vacationFromFirstRow,
            holidayItems: historyEntries?.items ?? [],
            lastYear: selectedYear,
            unusedVacationDaysAt31Dec2025: baselineDays,
            agreementTypeLabels,
        });
        return rows;
    }, [weeklyHoursSchedule, vacationFromFirstRow, historyEntries, selectedYear, baselineDays, agreementTypeLabels]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6">Histórico de férias</Typography>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {historyRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Sem histórico para mostrar.
                    </Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Ano</TableCell>
                                    <TableCell>Período</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell align="right">Marcado</TableCell>
                                    <TableCell align="right">Direito</TableCell>
                                    <TableCell align="right">Estado</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {historyRows.map((row, rowIndex) => {
                                    const isFirstRowOfYear = rowIndex === 0 || historyRows[rowIndex - 1].year !== row.year;
                                    return (
                                        <TableRow
                                            key={`${row.year}-${format(row.periodStartDate, "yyyy-MM-dd")}`}
                                            sx={isFirstRowOfYear && rowIndex > 0 ? { "& td": { borderTop: 2, borderTopColor: "divider" } } : {}}
                                        >
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                {isFirstRowOfYear ? row.year : ""}
                                                {isFirstRowOfYear && row.isBeforeBaseline && (
                                                    <Chip label="histórico" size="small" sx={{ ml: 1 }} variant="outlined" />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                <Typography variant="caption">
                                                    {row.isSettlement
                                                        ? "—"
                                                        : `${format(row.periodStartDate, "dd/MM")} → ${row.periodEndDate ? format(row.periodEndDate, "dd/MM") : "…"}`}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.typeLabel}
                                                </Typography>
                                            </TableCell>
                                            {row.isSettlement ? (
                                                <>
                                                    <TableCell />
                                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                        +{formatDisplayHours(row.entitlementHours, 0)}
                                                        <DaysSuffix hours={row.entitlementHours} dailyPattern={row.dailyPattern} />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="caption" color="text.secondary">
                                                            valor indicado pela contabilidade
                                                        </Typography>
                                                    </TableCell>
                                                </>
                                            ) : row.accrues ? (
                                                <>
                                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                        {formatDisplayHours(row.bookedHours, 0)}
                                                        <DaysSuffix hours={row.bookedHours} dailyPattern={row.dailyPattern} />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                                        {formatDisplayHours(row.entitlementHours, 0)}
                                                        <DaysSuffix hours={row.entitlementHours} dailyPattern={row.dailyPattern} />
                                                        {(row.carryInHours ?? 0) >= 0.5 && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{ display: "block" }}
                                                            >
                                                                +{formatDisplayHours(row.carryInHours ?? 0, 0)} de anos anteriores
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.isBeforeBaseline ? (
                                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                                                Acertado a 31/12/2025
                                                            </Typography>
                                                        ) : row.poolHours !== undefined ? (
                                                            <YearStateCell
                                                                entitlementHours={row.poolHours}
                                                                bookedHours={row.bookedHours}
                                                                dailyPattern={row.dailyPattern}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                                                Acertado na saída
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <TableCell align="right" colSpan={3}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Não se aplica
                                                    </Typography>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {historyRows.some((row) => row.isBeforeBaseline) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                        Os anos marcados como "histórico" são indicativos — a contagem foi acertada a 31/12/2025 e 2026 parte do
                        acerto oficial.{" "}
                        {baselineDays !== null && baselineDays !== undefined
                            ? `Acerto oficial: ${Number(baselineDays).toString().replace(".", ",")} dias por gozar.`
                            : "Sem dias por gozar no acerto — 2026 começa a zero."}
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};
